import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import {
  CLIENT_APP_ID,
  ClientAppId,
  Dossier,
  DossierComments,
  DossierCommentsEnum,
  Fitter,
  LocationDataGeocoded,
  findCorrespondingServiceType,
} from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { AdaptableOverlayService } from '@sc/feature-adaptable-overlay';
import { FitterNameDisplayService } from '@sc/feature-contextual-display';
import { DialogService } from '@sc/feature-dialog';
import {
  DossierManagerService,
  FitterCompleteDatePickerComponent,
} from '@sc/feature-fitter-shared-libs';
import {
  AsteriskInfoComponent,
  ButtonComponent,
  DossierSearchHistoryComponent,
  IconComponent,
  SelectComponent,
} from '@sc/ui-components';
import { AppointmentUtils, dayjs } from '@sc/utils';
import { Subject, Subscription, combineLatest, of } from 'rxjs';
import { delay, filter, map, scan, switchMap, take } from 'rxjs/operators';
import { BaseAbstractDirective } from '../../base-abstract';
import {
  PlanningCalendarService,
  PlanningFormSandboxService,
  PlanningSandboxService,
} from '../../services';
import { MatMenuModule } from '@angular/material/menu';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { IconName } from '@sc/data-access-assets';

@Component({
  selector: 'sc-planning-center-form',
  styleUrls: ['./planning-form-center.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FitterNameDisplayService],
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    NgIf,
    IconComponent,
    MatFormFieldModule,
    FitterCompleteDatePickerComponent,
    NgClass,
    MatSelectModule,
    NgFor,
    MatOptionModule,
    MatSlideToggleModule,
    AsyncPipe,
    TranslateModule,
    SelectComponent,
    AsteriskInfoComponent,
    MatMenuModule,
  ],
  template: `
    <ng-container [formGroup]="getAppointmentForm()">
      <div class="header-buttons  mb-l">
        <sc-button
          id="toggleDetailsBtn"
          class="toggle-details-btn"
          buttonType="mainWhite"
          (click)="onToggleDetailsClick($event)"
          leftIcon="reduce"
          >{{
            (isFormOpen
              ? 'PlanningFormCenter.toggleDetailsClose'
              : 'PlanningFormCenter.toggleDetailsOpen'
            ) | translate
          }}
        </sc-button>
        <sc-button
          id="closeDetailsBtn"
          class="close-details-btn"
          buttonType="mainWhite"
          (click)="onCloseDetailsClick($event)"
          leftIcon="close"
          >{{ 'PlanningFormCenter.closeFormBtn' | translate }}
        </sc-button>
        <sc-icon
          *ngIf="isAdmin() | async"
          name="info"
          (click)="openHistoryDialog()"
        ></sc-icon>
      </div>

      <fieldset>
        <div class="information-title mb-l">
          <legend>
            {{ 'PlanningFormCenter.informationTitle' | translate }}
          </legend>
          <sc-icon
            [name]="
              planningCalendarService.getOriginIcon(
                configurationService.getDossierGlobalOriginFromCreationData(
                  dossier?.creationData,
                  this.clientAppId
                )
              )
            "
          ></sc-icon>
        </div>

        <sc-asterisk-info></sc-asterisk-info>

        <mat-label class="text-grey-dark font-family-main font-s asterisk">{{
          'PlanningFormCenter.garageSelectLabel' | translate
        }}</mat-label>
        <sc-select
          class="centers mb-l"
          [value]="getAppointmentForm().get('fitterId')?.value"
          optionKey="id"
          [options]="fitters"
          [disabled]="form?.disabled"
          (valueChange)="onFitterSelect($event)"
        >
          <ng-template #triggerTemplate let-item>
            <ng-container *ngIf="getAppointmentForm().get('fitterId')?.value">{{
              getFitterSelectedValue(
                getAppointmentForm().get('fitterId')?.value
              ) | async
            }}</ng-container>
          </ng-template>

          <ng-template #optionTemplate let-item>
            <ng-container *ngIf="item?.id">{{
              getFitterSelectedValue(item?.id) | async
            }}</ng-container>
          </ng-template>
        </sc-select>

        <div
          class="time-error"
          *ngIf="!getAppointmentForm().get('fitterId').value"
        >
          {{ 'PlanningFormCenter.noGarageSelected' | translate }}
        </div>

        <sc-fitter-complete-date-picker
          class="mb-l"
          [form]="getAppointmentForm()"
          [addDelayToSuggestions]="true"
          [disabled]="form?.disabled"
          [touched]="touched"
        >
        </sc-fitter-complete-date-picker>

        <div
          class="location font-family-semi-bold font-s mb-l"
          [ngClass]="{
            inCenter: !getAppointmentForm().get('outsideIntervention')?.value,
            outsideIntervention: getAppointmentForm().get('outsideIntervention')
              ?.value
          }"
        >
          <sc-icon
            [name]="
              getAppointmentForm().get('outsideIntervention')?.value
                ? 'home'
                : 'fitter'
            "
          ></sc-icon>
          <span>{{ getFitterLocationTxt() }}</span>
        </div>

        <div
          class="location inCenter replacement-vehicle-info font-family-semi-bold font-s mb-l"
          *ngIf="
            getAppointmentForm().get('isReplacementVehicleSelected')?.value
          "
        >
          <sc-icon name="car"></sc-icon>
          <span>
            {{ 'PlanningFormCenter.withReplacementVehicle' | translate }}
          </span>
          <span
            *ngIf="displayAlertReplacementVehicle()"
            class="replacement-vehicle-alert-icon"
          >
            <sc-icon name="warning_circle"></sc-icon>
          </span>
        </div>

        <mat-label class="text-grey-dark font-family-main font-s">{{
          'PlanningFormCenter.resourceLabel' | translate
        }}</mat-label>
        <mat-select
          formControlName="assignedResources"
          class="resources mb-l"
          multiple
        >
          <mat-option
            *ngFor="let resource of availableTechnicians"
            [value]="resource.value"
            >{{
              'PlanningFormCenter.resourceValue'
                | translate: { value: resource.value, active: resource.active }
            }}</mat-option
          >
        </mat-select>

        <ng-container
          *ngIf="getAppointmentForm()?.get('outsideIntervention')?.value"
        >
          <mat-label class="text-grey-dark font-family-main font-s">{{
            'PlanningFormCenter.interventionVehicleLabel' | translate
          }}</mat-label>
          <sc-select
            class="centers mb-l"
            [value]="getInterventionVehicle()"
            [options]="availableInterventionVehicles"
            [disabled]="form?.disabled"
            (valueChange)="interventionVehicleChange($event)"
          >
            <ng-template #triggerTemplate let-item>
              {{
                'PlanningFormCenter.interventionVehicleValue'
                  | translate: getInterventionVehicle()
              }}
            </ng-template>

            <ng-template #optionTemplate let-item>
              {{
                'PlanningFormCenter.interventionVehicleValue'
                  | translate: { value: item.value, active: item.active }
              }}
            </ng-template>
          </sc-select>
        </ng-container>
      </fieldset>
      <div class="fixed-part">
        <fieldset>
          <div class="comment-title mb-l">
            <legend class="mb-l">
              {{ 'PlanningFormCenter.commentTitle' | translate }}
            </legend>
            <sc-icon
              *ngIf="getDisplayedComments()?.length"
              class="table_action_button"
              name="more_horiz"
              [matMenuTriggerFor]="sortMenu"
            >
            </sc-icon>
            <mat-menu #sortMenu="matMenu">
              <button
                class="menu-button"
                mat-menu-item
                (click)="sortComments('oldest')"
              >
                <span>{{
                  'PlanningFormCenter.commentSortBtnAsc' | translate
                }}</span>
              </button>
              <button
                class="menu-button"
                mat-menu-item
                (click)="sortComments('newest')"
              >
                <span>{{
                  'PlanningFormCenter.commentSortBtnDesc' | translate
                }}</span>
              </button>
            </mat-menu>
          </div>

          <div class="comment-container" *ngIf="getDisplayedComments()?.length">
            <div
              *ngFor="let comment of getDisplayedComments(); let i = index"
              class="comment-block"
              [ngClass]="{ 'comment-disabled': comment?.userType !== 'FITTER' }"
            >
              <div class="comment-header">
                <ng-container
                  *ngIf="
                    editingCommentIndex !== i || editingField !== 'userName'
                  "
                >
                  <span class="comment-user font-family-semi-bold">
                    <sc-icon
                      [name]="getUserTypeIconName(comment?.userType)"
                    ></sc-icon>
                    {{
                      comment.userType !== 'FITTER'
                        ? getUserTypeTranslation(comment?.userType)
                        : comment.userName
                    }}</span
                  >

                  <span class="comment-date font-s text-grey-dark">
                    - {{ formatCommentDate(comment.timestamp) }}</span
                  >
                  <ng-container *ngIf="comment.userType === 'FITTER'">
                    <button
                      type="button"
                      [matMenuTriggerFor]="commentMenu"
                      class="comment-menu-btn"
                    >
                      <sc-icon name="more_horiz"></sc-icon>
                    </button>
                    <mat-menu #commentMenu="matMenu">
                      <button mat-menu-item (click)="startEdit(i, 'comment')">
                        <span>{{
                          'PlanningFormCenter.editCommentText' | translate
                        }}</span>
                      </button>
                      <button mat-menu-item (click)="startEdit(i, 'userName')">
                        <span>{{
                          'PlanningFormCenter.editCommentAuthor' | translate
                        }}</span>
                      </button>
                      <button mat-menu-item (click)="deleteComment(i)">
                        <sc-icon name="delete"></sc-icon>
                        <span>{{
                          'PlanningFormCenter.deleteComment' | translate
                        }}</span>
                      </button>
                    </mat-menu>
                  </ng-container>
                </ng-container>
                <ng-container
                  *ngIf="
                    editingCommentIndex === i && editingField === 'userName'
                  "
                >
                  <sc-select
                    class="comment-author-select mb-s"
                    [value]="editingValue"
                    [options]="getCommentAuthors()"
                    (valueChange)="editingValue = $event"
                  >
                    <ng-template #triggerTemplate let-item>
                      {{ item || '' }}
                    </ng-template>
                    <ng-template #optionTemplate let-item>
                      {{ item }}
                    </ng-template>
                  </sc-select>
                  <span class="comment-edit-btns">
                    <sc-icon
                      class="comment-edit-btn-save"
                      name="check"
                      (click)="saveEdit()"
                    ></sc-icon>
                    <sc-icon
                      class="comment-edit-btn-cancel"
                      name="close"
                      (click)="cancelEdit()"
                    ></sc-icon
                  ></span>
                </ng-container>
              </div>
              <div class="comment-text ">
                <ng-container
                  *ngIf="
                    editingCommentIndex !== i || editingField !== 'comment'
                  "
                >
                  <span>{{ comment?.comment }}</span>
                </ng-container>
                <ng-container
                  *ngIf="
                    editingCommentIndex === i && editingField === 'comment'
                  "
                >
                  <textarea
                    class="comment-edit-textarea"
                    [value]="editingValue"
                    (input)="editingValue = $any($event.target)?.value"
                  ></textarea>
                  <span class="comment-edit-btns">
                    <sc-icon
                      class="comment-edit-btn-save"
                      name="check"
                      (click)="saveEdit()"
                    ></sc-icon>
                    <sc-icon
                      class="comment-edit-btn-cancel"
                      name="close"
                      (click)="cancelEdit()"
                    ></sc-icon>
                  </span>
                </ng-container>
              </div>
            </div>

            <sc-button
              *ngIf="hasMoreComments()"
              buttonType="mainWhite"
              (click)="loadMoreComments()"
              >{{
                'PlanningFormCenter.showMoreComments' | translate
              }}</sc-button
            >
          </div>
          <div class="add-comment-form mt-l">
            <h3 class="add-comment-title mb-s">
              {{ 'PlanningFormCenter.addCommentBtn' | translate }}
            </h3>
            <label class="add-comment-select mb-s" for="comment-author-select">
              {{ 'PlanningFormCenter.addCommentAs' | translate }}
            </label>
            <sc-select
              class="comment-author-select mb-s"
              [value]="getSelectedAuthorOption()"
              [options]="getCommentAuthors()"
              (valueChange)="newCommentAuthor = $event"
            >
              <ng-template #triggerTemplate let-item>
                {{ item || '' }}
              </ng-template>
              <ng-template #optionTemplate let-item>
                {{ item }}
              </ng-template>
            </sc-select>
            <ng-container class="add-comment-textarea-container">
              <textarea
                class="comment-input"
                [value]="newCommentText"
                (input)="newCommentText = $any($event.target).value"
                rows="3"
              ></textarea>
              <sc-button
                class="add-comment-submit-btn"
                [ngClass]="{
                  isOpenedBtn: isFormOpen
                }"
                leftIcon="geoloc"
                buttonType="primary"
                [iconOnly]="true"
                [disabled]="!newCommentAuthor || !newCommentText?.trim()"
                (click)="submitNewComment()"
              ></sc-button>
            </ng-container>
          </div>
        </fieldset>

        <!-- buttons for cancelled and no-show appointments (resumable) -->
        <div
          *ngIf="displayResumableApmtMode() && !isBlockedByConcurency()"
          class="resume-btn-container"
        >
          <sc-button
            id="resumeDossier"
            class="mb-l"
            buttonType="primary"
            leftIcon="edit"
            (click)="onResumeDossierClick()"
            >{{ 'PlanningFormCenter.resumeDossierButton' | translate }}
          </sc-button>
          <div *ngIf="isApmtNoShow()" class="form-apmt-done-container">
            <div class="apmt-error-infos">
              <sc-icon name="close"></sc-icon>
              <span>{{ 'PlanningFormCenter.noShowInfo' | translate }}</span>
            </div>
          </div>
        </div>

        <!-- buttons for edit mode dossiers -->
        <div *ngIf="!isBlockedByConcurency()" class="form-btn-container">
          <span class="save-btn-container">
            <span
              class="save-btns-menu"
              [ngClass]="{
                canSaveLight: canSaveLight()
              }"
              ><sc-button
                id="saveBtn"
                class="mb-l"
                [buttonType]="form.valid ? 'primary' : 'grey'"
                leftIcon="check_bis"
                *ngIf="!form?.disabled"
                (click)="saveDossier()"
                >{{ 'PlanningFormCenter.saveButton' | translate }}
              </sc-button>
              <sc-button
                id="saveBtn-menu"
                class="mb-l"
                [ngClass]="{
                  disabledStyle: !form.valid
                }"
                [matMenuTriggerFor]="menuSaveInBanet"
                [buttonType]="form.valid ? 'primary' : 'grey'"
                *ngIf="!form?.disabled && canSaveLight()"
                rightIcon="arrow_bottom"
              >
              </sc-button>

              <mat-menu #menuSaveInBanet="matMenu" xPosition="before">
                <button mat-menu-item (click)="saveLightDossier()">
                  <span>{{
                    'PlanningFormCenter.saveLightButton' | translate
                  }}</span>
                </button>
              </mat-menu>
            </span>
            <sc-button
              id="duplicateBtn"
              class="mb-l ml-l"
              buttonType="primary"
              leftIcon="plus"
              [iconOnly]="true"
              *ngIf="!form?.disabled"
              [disabled]="form?.invalid"
              (click)="duplicateDossier()"
            >
            </sc-button>
          </span>
          <sc-button
            *ngIf="isDraft()"
            id="draftDossierBtn"
            buttonType="mainWhite"
            (click)="saveDraftDossier()"
            >{{ 'PlanningFormCenter.saveDraftButton' | translate }}
          </sc-button>
          <sc-button
            *ngIf="isDraft()"
            id="abandonBtn"
            buttonType="errorWhite"
            leftIcon="delete"
            [disabled]="!canAbandondDossier()"
            (click)="onAbandonDossierClick($event)"
            >{{
              dossier?.id
                ? ('PlanningFormCenter.deleteDraftButton' | translate)
                : ('PlanningFormCenter.abandonButton' | translate)
            }}
          </sc-button>
        </div>

        <!-- buttons for completed dossiers -->
        <div *ngIf="displayCompletedMode()" class="form-btn-container">
          <div
            *ngIf="!isApmtDone() && !form?.disabled"
            class="form-btn-container mt-l"
          >
            <sc-button
              id="apmtDoneBtn"
              class="mb-l"
              buttonType="validation"
              leftIcon="check"
              (click)="onApmtDoneClick()"
              *ngIf="isAppointmentTodayOrPast() && !isRequestAppointment()"
              >{{ 'PlanningFormCenter.appointmentDoneButton' | translate }}
            </sc-button>
            <sc-button
              id="noShowBtn"
              class="mb-l"
              buttonType="noShow"
              leftIcon="no_show_appointment"
              (click)="onNoShowClientClick()"
              *ngIf="isAppointmentDatePast() && !isRequestAppointment()"
              >{{ 'PlanningFormCenter.noShowButton' | translate }}
            </sc-button>
            <sc-button
              id="cancelBtn"
              buttonType="errorDark"
              leftIcon="XCircle"
              (click)="onCancelAppointmentClick()"
              >{{
                'PlanningFormCenter.cancelButton'
                  | translate: { isRequestAppointment: isRequestAppointment() }
              }}
            </sc-button>
          </div>
          <div *ngIf="isApmtDone()" class="form-apmt-done-container">
            <div class="apmt-done-infos">
              <sc-icon name="check"></sc-icon>
              <span>{{
                'PlanningFormCenter.appointmentDoneInfo' | translate
              }}</span>
            </div>
            <mat-slide-toggle
              [checked]="isBilled()"
              class="filter-rv-text"
              [disableRipple]="true"
              [hideIcon]="true"
              [disabled]="isBlockedByConcurency()"
              (change)="onChangeBilling($event.checked)"
            >
              {{ 'PlanningFormCenter.billingCheckbox' | translate }}
            </mat-slide-toggle>
          </div>
        </div>
      </div>
    </ng-container>
  `,
})
export class PlanningFormCenterComponent
  extends BaseAbstractDirective
  implements OnInit, OnChanges
{
  @Input() fitters: Fitter[];
  @Input() isOpen = false;
  @Input() set initForm(form: FormGroup) {
    if (form) {
      this.form = form;
      this.initFormSubscription();
      this.changeDetector.markForCheck();
    }
  }
  @Input() dossier: Dossier;
  @Input() isFormOpen: boolean;
  @Input() touched: boolean;
  @Input() connectedUserName?: string | null;

  @Output() toggleDetailsEvent = new EventEmitter<any>();
  @Output() closeDetailsEvent = new EventEmitter<any>();
  @Output() validateEvent = new EventEmitter<any>();
  @Output() duplicateEvent = new EventEmitter<any>();
  @Output() abandonDossierEvent = new EventEmitter<any>();
  @Output() resumeDossierEvent = new EventEmitter<void>();
  @Output() noShowClickEvent = new EventEmitter<void>();
  @Output() apmtDoneClickEvent = new EventEmitter<void>();
  @Output() billingClickEvent = new EventEmitter<boolean>();
  @Output() cancelClickEvent = new EventEmitter<{
    comment: string;
    notifyClient: boolean;
  } | void>();
  @Output() saveDraftDossierEvent = new EventEmitter<void>();
  @Output() updateLightDossier = new EventEmitter<void>();
  @Output() saveChatComments = new EventEmitter<DossierComments[]>();

  form: FormGroup;
  isResumed = false;
  reloadTechnicians = new Subject<null>();
  reloadInterventionVehicles = new Subject<null>();
  availableTechnicians: { value: string; active: boolean }[] = [];
  availableInterventionVehicles: { value: string; active: boolean }[] = [
    { value: null, active: true },
  ];

  private formSubs = new Subscription();

  sortedComments: DossierComments[] = [];
  displayedCommentsCount = 5;
  commentSortOrder: 'oldest' | 'newest' = 'oldest';
  newCommentAuthor: string | null = null;
  newCommentText = '';
  editingCommentIndex: number | null = null;
  editingField: 'comment' | 'userName' | null = null;
  editingValue = '';

  constructor(
    injector: Injector,
    private readonly translator: TranslatorService,
    public planningCalendarService: PlanningCalendarService,
    private readonly timezoneService: TimezoneService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly planningSandbox: PlanningSandboxService,
    private readonly dialogManager: DialogService,
    private readonly planningFormSB: PlanningFormSandboxService,
    private readonly dossierManagerService: DossierManagerService,
    public configurationService: ConfigurationService,
    private readonly overlayDialogService: AdaptableOverlayService,
    private readonly fitterNameDisplayService: FitterNameDisplayService,
    public featureFlippingService: FeatureFlippingManagerService,
    @Inject(CLIENT_APP_ID) public clientAppId: ClientAppId
  ) {
    super(injector);
  }

  ngOnInit() {
    this.initializeNewCommentAuthor();

    this.reloadTechnicians
      .pipe(switchMap(() => this.getFitterRessources()))
      .subscribe((usedTechnicians) => {
        this.availableTechnicians = usedTechnicians.map((technician) => {
          return { value: technician, active: true };
        });
        if (this?.dossier?.appointment?.assignedResources?.length) {
          for (const technician of this.dossier.appointment.assignedResources) {
            if (!usedTechnicians.includes(technician))
              this.availableTechnicians.push({
                value: technician,
                active: false,
              });
          }
        }
        this.changeDetector.markForCheck();
      });

    this.reloadInterventionVehicles
      .pipe(switchMap(() => this.getFitterInterventionVehicles()))
      .subscribe((usedInterventionVehicles) => {
        this.availableInterventionVehicles = usedInterventionVehicles.map(
          (interventionVehicle) => {
            return { value: interventionVehicle, active: true };
          }
        );

        if (
          this.dossier?.appointment?.assignedInterventionVehicle &&
          this.getAppointmentForm().get('fitterId')?.value ===
            this.dossier.appointment.fitterId &&
          !usedInterventionVehicles.includes(
            this.dossier?.appointment?.assignedInterventionVehicle
          )
        ) {
          this.availableInterventionVehicles.unshift({
            value: this.dossier.appointment.assignedInterventionVehicle,
            active: false,
          });
        }

        this.availableInterventionVehicles.unshift({
          value: null,
          active: true,
        });
        this.changeDetector.markForCheck();
      });

    this.reloadTechnicians.next(null);
    this.reloadInterventionVehicles.next(null);

    this.initFormSubscription();

    //set center on a new intervention if there is only one
    if (this.dossier?.id === null && this.fitters.length === 1) {
      this.onFitterSelect(this.fitters[0].id);
    }

    this.initSortedComments();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['connectedUserName']) {
      this.initializeNewCommentAuthor();
    }
  }

  initFormSubscription() {
    this.formSubs.unsubscribe();
    this.formSubs = new Subscription();
    this.subs['formSubs'] = this.formSubs;

    const formReloadSub = this.form?.valueChanges.subscribe(() => {
      this.changeDetector.markForCheck();
    });

    const apmtReloadSub = this.form
      ?.get('appointment')
      ?.valueChanges.subscribe(() => {
        this.reloadTechnicians.next(null);
        this.reloadInterventionVehicles.next(null);
        this.changeDetector.markForCheck();
      });

    this.formSubs.add(formReloadSub);
    this.formSubs.add(apmtReloadSub);
  }

  getInterventionVehicle() {
    const value =
      this.getAppointmentForm().get('assignedInterventionVehicle')?.value ??
      null;
    return this.availableInterventionVehicles.find(
      (interventionVehicle) => interventionVehicle.value === value
    );
  }

  interventionVehicleChange($event) {
    this.getAppointmentForm()
      ?.get('assignedInterventionVehicle')
      .setValue($event.value);
  }

  onAbandonDossierClick($event) {
    this.abandonDossierEvent.emit($event);
  }

  onToggleDetailsClick($event) {
    this.toggleDetailsEvent.emit($event);
  }

  saveDossier() {
    this.validateEvent.emit(this.isResumed);
    this.isResumed = false;
  }

  duplicateDossier() {
    this.duplicateEvent.emit();
  }

  isAbandonnedDossier() {
    return this.planningCalendarService.isDossierAbandoned(this.dossier);
  }

  isCancelledDossier() {
    return this.planningCalendarService.isDossierCancelled(this.dossier);
  }

  isApmtDone() {
    return this.planningCalendarService.isAppointmentDone(this.dossier);
  }

  isApmtNoShow() {
    return this.planningCalendarService.isDossierNoShow(this.dossier);
  }

  isBilled() {
    return this.dossier?.fitterInfo?.isReadyToBill;
  }

  isDraft() {
    return this.planningCalendarService.isDossierDraft(this.dossier);
  }

  isBlockedByConcurency() {
    return this.dossierManagerService.dossierOpennedBySomeoneElse(this.dossier);
  }

  displayResumableApmtMode() {
    return (
      !this.isAbandonnedDossier() &&
      (this.isCancelledDossier() || this.isApmtNoShow()) &&
      this.form?.disabled
    );
  }

  displayCompletedMode() {
    return (
      (this.dossier?.state === 'COMPLETE' ||
        this.dossier?.state === 'DONE' ||
        this.dossier?.appointment?.isValidatedByFitter ||
        this.isRequestAppointment()) &&
      !this.isCancelledDossier() &&
      !this.isAbandonnedDossier() &&
      !this.isApmtNoShow() &&
      !this.isDraft()
    );
  }

  displayAlertReplacementVehicle() {
    return (
      !!this.getAppointmentForm()?.get('isReplacementVehicleSelected')?.value &&
      !this.getAppointmentForm().get('assignedReplacementVehicle')?.value
    );
  }

  onFitterSelect(fitterId: string) {
    const fitter = this.fitters?.find((fitter) => fitter?.id === fitterId);

    const appointmentForm = this.getAppointmentForm();
    appointmentForm
      ?.get('assignedResources')
      ?.setValue(
        appointmentForm
          ?.get('assignedResources')
          ?.value?.filter((tech) =>
            fitter.resources?.some(
              (fitterTech) => tech === fitterTech.name && !!fitterTech.active
            )
          )
      );
    appointmentForm
      ?.get('assignedInterventionVehicle')
      ?.setValue(
        appointmentForm
          ?.get('assignedInterventionVehicle')
          ?.value?.filter((iv) =>
            fitter.interventionVehicles?.some(
              (fitterIV) => iv === fitterIV.name && !!fitterIV.active
            )
          )
      );
    const appointment = appointmentForm?.value;
    const claim = this.form.get('claim')?.value;

    const newFitterIntervention = findCorrespondingServiceType(
      {
        id: appointment?.interventionId,
        glassType: claim?.glass?.glass,
        operationType: claim?.operationType,
        isMobility: appointment?.outsideIntervention,
        replacementVehicle: appointment?.replacementVehicle,
        active: true,
        calibration: claim?.calibration === 'true',
      },
      fitter?.serviceTypes || []
    );

    this.getAppointmentForm()?.get('fitterId')?.setValue(fitterId);
    this.getAppointmentForm()?.get('brandId')?.setValue(fitter?.brandId);
    this.getAppointmentForm()
      ?.get('timezone')
      ?.setValue(this.timezoneService.getFitterTimezone(fitter));

    const interventionLocation =
      this.getAppointmentForm()?.get('outsideIntervention')?.value ||
      this.getAppointmentForm()?.get('isConveying')?.value
        ? this.getAppointmentForm()?.get('interventionLocation')?.value
        : AppointmentUtils.getFitterLocation(fitter);

    this.getAppointmentForm()
      .get('interventionLocation')
      .setValue(interventionLocation);
    this.planningFormSB.selectNewServiceType(newFitterIntervention ?? null);
  }

  getFitterSelectedValue(fitterId: string) {
    if (fitterId) {
      const fitter = this.fitters?.find((fitter) => fitter?.id === fitterId);

      if (fitter) {
        return this.fitterNameDisplayService.getFitterNameTranslation(fitter);
      }
    }

    return of('-');
  }

  getFitterLocationTxt() {
    if (this.getAppointmentForm().get('outsideIntervention')?.value) {
      const interventionLocation: LocationDataGeocoded =
        this.getAppointmentForm().get('interventionLocation')?.value;
      return interventionLocation
        ? interventionLocation?.address ||
            interventionLocation?.addressElements?.city
        : this.translator.getInstantTranslation('PlanningFormCenter.atHome');
    } else {
      return this.translator.getInstantTranslation(
        'PlanningFormCenter.inCenter'
      );
    }
  }

  getFitterRessources() {
    const fitter = this.fitters?.find(
      (fitter) =>
        fitter?.id === this.getAppointmentForm()?.get('fitterId').value
    );

    const resToUse$ = of(...(fitter?.resources || [])).pipe(
      delay(100), // This delay allows us to not overload the CPU with too much searching in calendar events at the same time --> DO NOT REMOVE
      switchMap((resource) =>
        of(
          this.planningSandbox.isTechnicianUnavailable(
            this.dossier,
            resource,
            true // NOTE - allow multiple dossier for one technician at the same dayjs, unless they are on leave
          )
        ).pipe(
          map((isUnavailable) => ({
            resource,
            canUse: resource?.active && !isUnavailable,
          })),
          filter((res) => res.canUse)
        )
      ),
      map((res) => res.resource?.name),
      scan((acc: string[], cur) => [...acc, cur], [])
    );

    return resToUse$;
  }

  getFitterInterventionVehicles() {
    const fitter = this.fitters?.find(
      (fitter) =>
        fitter?.id === this.getAppointmentForm()?.get('fitterId').value
    );

    const vehiclesToUse$ = of(...(fitter?.interventionVehicles || [])).pipe(
      delay(100), // This delay allows us to not overload the CPU with to much searching in calendar events at the same time --> DO NOT REMOVE
      switchMap((interventionVehicle) =>
        of(
          this.planningSandbox.isInterventionVehicleUnavailable(
            this.dossier,
            interventionVehicle,
            true
          )
        ).pipe(
          map((isUnavailable) => ({
            interventionVehicle,
            canUse: interventionVehicle?.active && !isUnavailable,
          })),
          filter((vehicle) => vehicle.canUse)
        )
      ),
      map((res) => res.interventionVehicle?.name),
      scan((acc: string[], cur) => [...acc, cur], [])
    );

    return vehiclesToUse$;
  }

  onCloseDetailsClick($event) {
    this.closeDetailsEvent.emit($event);
  }

  getAppointmentForm(): FormGroup {
    return this.form?.get('appointment') as FormGroup;
  }

  onNoShowClientClick() {
    this.noShowClickEvent.emit();
  }

  onApmtDoneClick() {
    this.overlayDialogService
      .showOverlay({ type: 'complete-appointment', input: null })
      .subscribe((result) => {
        if (result === 'success') {
          this.apmtDoneClickEvent.emit();
        }
      });
  }

  onResumeDossierClick() {
    this.isResumed = true;
    this.resumeDossierEvent.emit();
  }

  onChangeBilling(newBillingValue: boolean) {
    this.billingClickEvent.emit(newBillingValue);
  }

  onCancelAppointmentClick() {
    if (
      this.featureFlippingService.isFeatureActivated(
        'appointment-canceling-reason'
      )
    ) {
      this.showCancelAppointmentOverlay();
    } else {
      this.cancelClickEvent.emit();
      this.isResumed = false;
    }
  }

  private showCancelAppointmentOverlay() {
    this.overlayDialogService
      .showOverlay({
        type: 'cancel-appointment',
        input: null,
      })
      .pipe(
        filter((overlayOutput) => overlayOutput.type === 'success'),
        take(1)
      )
      .subscribe({
        next: (overlayOutput) => {
          if ('data' in overlayOutput) {
            this.cancelClickEvent.emit({
              comment: overlayOutput.data?.comment,
              notifyClient: overlayOutput.data?.notifyClient,
            });
          } else {
            this.cancelClickEvent.emit();
          }
          this.isResumed = false;
        },
        error: (error) => {
          console.error('Error in cancel appointment overlay:', error);
        },
      });
  }

  isRequestAppointment() {
    return !!this.dossier?.requestAppointment && !this.dossier?.appointment;
  }

  canSaveLight() {
    return (
      this.isRequestAppointment() ||
      (this.dossier?.appointment?.id &&
        !this.dossier?.appointment?.isValidatedByFitter)
    );
  }

  isNewDossierBegun() {
    return (
      !this.dossier?.id &&
      !!this.form?.get('customer')?.get('lastname')?.value &&
      !!this.form?.get('customer')?.get('phoneNumber')?.get('number')?.value &&
      !!this.form?.get('appointment')?.get('fitterId')?.value
    );
  }

  canAbandondDossier() {
    const isSavedDraft = this.isDraft() && !!this.dossier?.id;
    return isSavedDraft || this.isNewDossierBegun();
  }

  isAppointmentTodayOrPast() {
    return dayjs().isAfter(
      dayjs(this.dossier?.appointment?.time)
        .utcOffset(
          this.timezoneService.getFitterTimezone(
            this.fitters.find(
              (fitter) => fitter.id === this.dossier?.appointment?.fitterId
            )
          )
        )
        .startOf('day')
    );
  }

  isAppointmentDatePast() {
    return new Date() > new Date(this.dossier?.appointment?.endTime);
  }

  saveDraftDossier() {
    this.saveDraftDossierEvent.emit();
  }

  openHistoryDialog() {
    this.dialogManager.showDialog(DossierSearchHistoryComponent, {
      dossier: this.dossier,
    });
  }

  isAdmin() {
    const fitter = this.fitters?.find(
      (fitter) => fitter?.id === this.getAppointmentForm()?.value?.fitterId
    );
    return combineLatest([
      this.planningSandbox.userIsSuperAdmin(),
      this.planningSandbox.userHasFitterAdminGroupAccess(fitter),
    ]).pipe(
      map(([isSuperAdmin, isFitterAdmin]) => isSuperAdmin || isFitterAdmin)
    );
  }

  saveLightDossier() {
    this.updateLightDossier.emit();
  }

  canAddComments() {
    return !!this.dossier?.id;
  }

  sortComments(order: 'oldest' | 'newest') {
    this.commentSortOrder = order;
    this.sortedComments = this.sortCommentsByOrder(this.sortedComments);
    this.changeDetector.detectChanges();
  }

  initSortedComments() {
    this.sortedComments = this.sortCommentsByOrder([
      ...(this.dossier?.dossierComments || []),
    ]);
    this.displayedCommentsCount = 5;
    this.changeDetector.markForCheck();
  }

  sortCommentsByOrder(comments: DossierComments[]): DossierComments[] {
    return [...comments].sort((a, b) => {
      const diff =
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      return this.commentSortOrder === 'oldest' ? diff : -diff;
    });
  }

  getDisplayedComments(): DossierComments[] {
    return this.sortedComments?.slice(0, this.displayedCommentsCount);
  }

  hasMoreComments(): boolean {
    return (this.sortedComments?.length || 0) > this.displayedCommentsCount;
  }

  loadMoreComments() {
    this.displayedCommentsCount += 5;
    this.changeDetector.markForCheck();
  }

  formatCommentDate(timestamp: string): string {
    const date = new Date(timestamp);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}/${pad(
      date.getMonth() + 1
    )}/${date.getFullYear()} - ${pad(date.getHours())}:${pad(
      date.getMinutes()
    )}`;
  }

  startEdit(index: number, field: 'comment' | 'userName') {
    this.editingCommentIndex = index;
    this.editingField = field;
    this.editingValue = this.sortedComments[index][field] as string;
    this.changeDetector.markForCheck();
  }

  saveEdit() {
    if (this.editingCommentIndex !== null && this.editingField) {
      this.sortedComments[this.editingCommentIndex][this.editingField] =
        this.editingValue;
      this.emitComments();
      this.cancelEdit();
    }
  }

  cancelEdit() {
    this.editingCommentIndex = null;
    this.editingField = null;
    this.editingValue = '';
    this.changeDetector.markForCheck();
  }

  deleteComment(index: number) {
    this.sortedComments.splice(index, 1);
    this.emitComments();
    this.changeDetector.markForCheck();
  }

  emitComments() {
    if (!this.dossier?.id) {
      this.form.get('dossierComments')?.setValue([...this.sortedComments]);
    } else {
      this.saveChatComments.emit([...this.sortedComments]);
    }
  }

  submitNewComment() {
    const newComment: DossierComments = {
      userName: this.newCommentAuthor ?? '',
      userType: 'FITTER',
      comment: this.newCommentText,
      timestamp: new Date().toISOString(),
      commentType: DossierCommentsEnum.other,
      isPinned: false,
    };
    this.sortedComments = this.sortCommentsByOrder([
      ...this.sortedComments,
      newComment,
    ]);
    this.newCommentText = '';
    this.newCommentAuthor = this.connectedUserName ?? null;
    this.emitComments();
    this.changeDetector.markForCheck();
  }

  private initializeNewCommentAuthor() {
    if (!this.newCommentAuthor && this.connectedUserName) {
      this.newCommentAuthor = this.connectedUserName;
    }
  }

  getCommentAuthors(): string[] {
    const authors: string[] = [];
    if (this.connectedUserName) {
      authors.push(this.connectedUserName);
    }
    const fitter = this.fitters?.find(
      (f) => f?.id === this.getAppointmentForm()?.get('fitterId')?.value
    );
    fitter?.resources
      ?.filter((r) => r?.active && r?.name !== this.connectedUserName)
      ?.forEach((r) => authors.push(r?.name));
    return authors;
  }

  getSelectedAuthorOption(): string | null | undefined {
    return this.newCommentAuthor
      ? this.getCommentAuthors().find((a) => a === this.newCommentAuthor) ??
          null
      : this.connectedUserName;
  }

  getUserTypeIconName(userType: string): IconName {
    switch (userType) {
      case 'INSURER':
        return 'insurer';
      case 'CUSTOMER':
        return 'user';
      case 'FITTER':
        return 'fitter';
      default:
        return 'group';
    }
  }

  getUserTypeTranslation(userType: string): string {
    return (
      this.translator.getInstantTranslation(
        'GlobalApplicativeConstants.userType.' + (userType || 'OTHER')
      ) || ''
    );
  }
}
